import React, { useEffect, useState, useRef } from 'react';
import { getPageBySlug } from '../../utils/wordpressApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './About.css';
import defaultPhoto from '../../assets/images/default-avatar.jpg';

const fallbackData = {
  intro: 'Poznaj naszą historię, misję i zespół specjalistów',
  stats: [
    { number: '20+', label: 'Lat doświadczenia' },
    { number: '500+', label: 'Zrealizowanych projektów' }
  ],
  team: [
    {
      name: 'Rafał Łopatka',
      role: 'Właściciel, Inżynier elektryk',
      description: '20 lat doświadczenia w branży',
      photo: defaultPhoto
    }
  ]
};

const About = () => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parsedData, setParsedData] = useState(fallbackData);

  // Refs dla animacji
  const headerRef = useRef(null);
  const statsRef = useRef(null);
  const teamRef = useRef(null);
  const infoRef = useRef(null);

  useEffect(() => {
    const fetchAboutPage = async () => {
      try {
        setLoading(true);
        const aboutPage = await getPageBySlug('about');
        setPage(aboutPage);
        
        if (aboutPage?.content?.rendered) {
          parseWordPressContent(aboutPage.content.rendered);
        }
      } catch (err) {
        // zostaje fallbackData
      } finally {
        setLoading(false);
      }
    };

    fetchAboutPage();
  }, []);

  // Hook dla animacji po załadowaniu
  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        animateElements();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const animateElements = () => {
    if (headerRef.current) {
      const headerContent = headerRef.current.querySelector('.header-content');
      if (headerContent) headerContent.classList.add('animated');
    }
    if (statsRef.current) {
      const statItems = statsRef.current.querySelectorAll('.stat-item');
      statItems.forEach((item, index) => {
        setTimeout(() => item.classList.add('animated'), index * 150);
      });
    }
    if (teamRef.current) {
      const sectionTitle = teamRef.current.querySelector('.section-title');
      if (sectionTitle) setTimeout(() => sectionTitle.classList.add('animated'), 300);
      const teamMembers = teamRef.current.querySelectorAll('.team-member');
      teamMembers.forEach((member, index) => {
        setTimeout(() => member.classList.add('animated'), 500 + (index * 150));
      });
    }
    if (infoRef.current) {
      const infoContent = infoRef.current.querySelector('.info-content');
      if (infoContent) setTimeout(() => infoContent.classList.add('animated'), 800);
    }
  };

  const parseWordPressContent = (htmlContent) => {
    // tutaj zostaje Twój parser – on nadpisze fallbackData jeśli WordPress odpowie
    // ...
  };

  if (loading) {
    return <LoadingSpinner text="Ładowanie informacji o firmie..." />;
  }

  return (
    <div className="about-page">
      {error && (
        <div className="error-container">
          <p>{error}</p>
        </div>
      )}

      {/* Page Header */}
      <section className="page-header" ref={headerRef}>
        <div className="container">
          <div className="header-content">
            <h1>{page?.title?.rendered || 'O firmie'}</h1>
            <p>{parsedData.intro}</p>
          </div>
        </div>
      </section>

      {/* Company Stats */}
      <section className="stats-section section" ref={statsRef}>
        <div className="container">
          <div className="stats-grid">
            {parsedData.stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section section" ref={teamRef}>
        <div className="container">
          <h2 className="section-title">Nasz zespół</h2>
          <div className="team-grid">
            {parsedData.team.map((member, index) => (
              <div key={index} className="team-member">
                <div className="member-photo">
                  <img 
                    src={member.photo} 
                    alt={member.name}
                    onError={(e) => { e.target.src = defaultPhoto; }}
                  />
                </div>
                <h3>{member.name}</h3>
                <p className="member-role">{member.role}</p>
                <p className="member-description">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info Section – zostaje jak w Twoim kodzie */}
      <section className="additional-info-section section" ref={infoRef}>
        <div className="container">
          <div className="info-content">
            <h2>Dlaczego warto z nami współpracować?</h2>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-icon">🏆</div>
                <h3>Doświadczenie</h3>
                <p>Ponad dwie dekady pracy w branży elektrycznej i teletechnicznej</p>
              </div>
              <div className="info-item">
                <div className="info-icon">⚡</div>
                <h3>Nowoczesne rozwiązania</h3>
                <p>Wykorzystujemy najnowsze technologie i certyfikowane komponenty</p>
              </div>
              <div className="info-item">
                <div className="info-icon">🔧</div>
                <h3>Kompleksowość</h3>
                <p>Od projektu przez realizację po serwis - wszystko w jednym miejscu wraz z pełną dokumentacją powykonawczą</p>
              </div>
              <div className="info-item">
                <div className="info-icon">🛡️</div>
                <h3>Gwarancja jakości</h3>
                <p>Pełne ubezpieczenie i długoterminowa gwarancja na nasze usługi</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
